import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from '@remix-run/react';
import { useStore } from '~/context/store-context';
import { useCart } from '~/context/cart-context';
import { useFocusTrap } from '~/hooks/use-focus-trap';
import { useStores } from '~/hooks/useStores';

interface Store {
  name: string;
  value: string;
  url: string;
  /** This store's other location pages — see Store.altUrls in constants/stores. */
  altUrls?: string[];
  address: string;
  hours: string;
  comingSoon?: boolean;
}

interface StoreSelectorProps {
  onStoreSelect?: (storeValue: string) => void;
  onModalOpen?: () => void;
}

export default function StoreSelector({ onStoreSelect, onModalOpen }: StoreSelectorProps) {
  const { selectedStore, setSelectedStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount } = useCart();
  const [showModal, setShowModal] = useState(false);
  const [chosenStore, setChosenStore] = useState(selectedStore);
  useEffect(() => {
    if (showModal) setChosenStore(selectedStore);
  }, [showModal, selectedStore]);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pendingStore, setPendingStore] = useState<string | null>(null);
  const [pendingRedirect, setPendingRedirect] = useState<boolean>(false);
  const [zipCode, setZipCode] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const scrollYRef = useRef<number>(0);
  // Flag to skip URL auto-detection during manual store selection
  const isManualSelectionRef = useRef<boolean>(false);
  // Ref for the modal's scrollable container.
  const modalScrollRef = useRef<HTMLDivElement>(null);
  const storeSelectorFocusTrapRef = useFocusTrap(showModal);
  const confirmModalFocusTrapRef = useFocusTrap(confirmModalVisible);
  const previousFocus = useRef<HTMLElement | null>(null);

  // Store the previously focused element when opening the modal
  useEffect(() => {
    if (showModal) {
      previousFocus.current = document.activeElement as HTMLElement;
      setAnnouncement('Store selector opened. Use Tab to navigate, Escape to close.');
    } else if (previousFocus.current) {
      previousFocus.current.focus();
      setAnnouncement('Store selector closed.');
    }
  }, [showModal]);

  // Listen for close event from focus trap
  useEffect(() => {
    const handleClose = () => setShowModal(false);
    document.addEventListener('close-focus-trap', handleClose);
    return () => document.removeEventListener('close-focus-trap', handleClose);
  }, []);

  // Stores come from the root loader (Store Central + website_locations merged,
  // or hardcoded fallback). Hour overrides are applied inside the adapter.
  const baseStores = useStores();

  // Map to the simplified Store interface used in this component
  const stores: Store[] = baseStores.map((store) => ({
    name: store.name,
    value: store.value,
    address: store.address,
    url: store.url,
    altUrls: store.altUrls,
    hours: store.hours,
    comingSoon: store.comingSoon,
  }));

  const currentStore = stores.find((store) => store.value === selectedStore);

  const needsStore = !currentStore || !!currentStore.comingSoon;
  useEffect(() => {
    const open = () => setShowModal(true);
    window.addEventListener('store:select', open);
    return () => window.removeEventListener('store:select', open);
  }, []);

  useEffect(() => {
    const catalogue =
      location.pathname === '/' || /^\/(shop|brand|category|deals)(\/|$)/.test(location.pathname);
    // The first-visit age gate already includes store selection. Opening both
    // dialogs makes their focus traps cancel touch scrolling in each other.
    const ageApproved = document.cookie.split('; ').some((row) => row.startsWith('ageApproved='));
    if (catalogue && needsStore && ageApproved) setShowModal(true);
  }, [location.pathname, needsStore]);

  // If the currently selected store is coming soon, clear it to prevent issues
  const isCurrentStoreComingSoon = currentStore?.comingSoon;

  // Clear coming soon store selection on mount to prevent 500 errors
  useEffect(() => {
    if (isCurrentStoreComingSoon && selectedStore) {
      // Clear the problematic store selection
      localStorage.removeItem('selectedStore');
      document.cookie = 'selectedStore=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      setSelectedStore('');
      window.dispatchEvent(new Event('store:change'));
    }
  }, [isCurrentStoreComingSoon, selectedStore, setSelectedStore]);

  // Open the modal when the dropdown is clicked.
  const openModal = () => {
    onModalOpen?.();
    setShowModal(true);
  };

  // Auto-detect store based on URL.
  useEffect(() => {
    // Skip URL auto-detection during manual store selection to prevent race conditions
    if (isManualSelectionRef.current) return;
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      // Normalize pathname by removing trailing slashes for comparison
      const normalizedPathname = pathname.replace(/\/+$/, '');
      // Match the store's own page OR any of its other pages, so landing on
      // Hawthorne's Cannabis Lounge page still selects the Hawthorne store.
      const storeMatch = stores.find((store) => {
        if (store.comingSoon) return false;
        const paths = [store.url, ...(store.altUrls ?? [])];
        return paths.some((p) => p.replace(/\/+$/, '') === normalizedPathname);
      });
      if (storeMatch && storeMatch.value !== selectedStore) {
        (async () => {
          setSelectedStore(storeMatch.value);
          localStorage.setItem('selectedStore', storeMatch.value);
          document.cookie = `selectedStore=${storeMatch.value}; path=/; max-age=${60 * 60 * 24 * 365}`;

          window.dispatchEvent(new Event('store:change'));
        })();
      }
    }
  }, [selectedStore, setSelectedStore]);

  // Listen for global store changes.
  useEffect(() => {
    const handleStoreChange = () => {
      const cookieMatch = document.cookie
        .split('; ')
        .find((row) => row.startsWith('selectedStore='));
      if (cookieMatch) {
        const storeFromCookie = cookieMatch.split('=')[1];
        setSelectedStore(storeFromCookie);
      }
    };

    window.addEventListener('store:change', handleStoreChange);
    return () => window.removeEventListener('store:change', handleStoreChange);
  }, [setSelectedStore]);

  // Prevent background scrolling with scrollbar width compensation to avoid layout shift
  useEffect(() => {
    if (showModal) {
      // Save current scroll position
      scrollYRef.current = window.scrollY;

      // Calculate scrollbar width and compensate to prevent layout shift
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollBarWidth}px`;
      // Also prevent scrolling on the root element
      document.documentElement.style.overflow = 'hidden';
      // Prevent zoom on double tap for modal overlays
      document.body.style.touchAction = 'manipulation';
    } else {
      // Restore all scroll-related styles
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';

      // Restore scroll position if it changed
      if (scrollYRef.current !== window.scrollY) {
        window.scrollTo(0, scrollYRef.current);
      }
    }

    return () => {
      // Cleanup - restore all scroll-related styles
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
    };
  }, [showModal]);

  // When a store button is clicked.
  const handleStoreSelection = (storeValue: string) => {
    // Don't allow selection of coming soon stores
    const store = stores.find((s) => s.value === storeValue);
    if (!store || store.comingSoon) {
      return;
    }

    // Check if we're on a dispensary locations page
    const isOnLocationsPage = location.pathname.includes('/dispensary-locations/');

    if (storeValue !== selectedStore && cartCount > 0) {
      setPendingStore(storeValue);
      setConfirmModalVisible(true);
      setPendingRedirect(isOnLocationsPage);
    } else {
      if (isOnLocationsPage) {
        handleStoreSelectAndRedirect(storeValue);
      } else {
        handleStoreSelectAndConfirm(storeValue);
      }
    }
  };

  const handleStoreSelectAndConfirm = async (storeValue: string) => {
    if (!stores.some((store) => store.value === storeValue && !store.comingSoon)) return;
    try {
      setSelectedStore(storeValue);
      localStorage.setItem('selectedStore', storeValue);
      document.cookie = `selectedStore=${storeValue}; path=/; max-age=${60 * 60 * 24 * 365}`;
      document.cookie = `ageApproved=true; path=/; max-age=${60 * 60 * 24 * 365}`;

      if (onStoreSelect) onStoreSelect(storeValue);
      window.dispatchEvent(new Event('store:change'));

      // Close the modal after selecting a store.
      setShowModal(false);
    } catch (error) {
      console.error('Error confirming store selection:', error);
    }
  };

  // New function to handle store selection with redirect
  const handleStoreSelectAndRedirect = async (storeValue: string) => {
    try {
      // Prevent URL auto-detection from overriding the manual selection
      isManualSelectionRef.current = true;
      // Check if we're on a dispensary locations page
      const isOnLocationsPage = location.pathname.includes('/dispensary-locations/');

      if (isOnLocationsPage) {
        // Find the selected store's locations page URL
        const selectedStore = stores.find((s) => s.value === storeValue);
        if (selectedStore?.url) {
          // Set a flag to prevent the locations page from overriding our selection
          localStorage.setItem('userSelectedStore', 'true');
          // Set store and cookies first
          await handleStoreSelectAndConfirm(storeValue);
          // Redirect immediately to the correct locations page
          window.location.href = selectedStore.url;
        } else {
          // Fallback to homepage if store URL not found
          window.location.href = '/';
        }
      } else {
        // If not on locations page, redirect to homepage
        await handleStoreSelectAndConfirm(storeValue);
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error during store selection and redirect:', error);
    }
  };

  const cancelConfirmation = () => {
    setConfirmModalVisible(false);
    setPendingStore(null);
    setPendingRedirect(false);
  };

  const confirmStoreChange = async () => {
    if (pendingStore) {
      if (pendingRedirect) {
        await handleStoreSelectAndRedirect(pendingStore);
      } else {
        await handleStoreSelectAndConfirm(pendingStore);
      }
      setConfirmModalVisible(false);
      setPendingStore(null);
      setPendingRedirect(false);
    }
  };

  const pendingStoreObj = stores.find((store) => store.value === pendingStore);

  return (
    <>
      {/* ARIA Live Region for Store Selector Announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      {/* Dropdown trigger */}
      <button
        type="button"
        className="font-magda dropdown-button flex cursor-pointer items-center justify-center select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
        onClick={openModal}
        aria-expanded={showModal}
        aria-controls="store-selector-modal"
        aria-label={`Select store. Currently shopping at ${currentStore ? currentStore.name : 'no store selected'}`}
      >
        <span className="text-catalystblue font-helvetica-neue flex h-5 min-w-[120px] items-center text-left text-sm leading-5 uppercase md:text-base">
          {currentStore ? currentStore.name : 'CHOOSE STORE'}
        </span>
        <svg
          className="ml-6 h-4 w-4 transition-transform duration-300 motion-reduce:transition-none"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
          role="presentation"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Store selector modal */}
      {showModal && (
        <div
          id="store-selector-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="store-selector-title"
          className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black/50"
          onClick={() => setShowModal(false)}
        >
          <div
            ref={storeSelectorFocusTrapRef}
            className="fixed top-10 left-1/2 z-50 w-full max-w-[480px] -translate-x-1/2 transform bg-white shadow-md"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'grid',
              gridTemplateRows: 'auto minmax(0, 1fr) auto',
              maxHeight: '82vh',
            }}
            role="document"
          >
            {/* Header */}
            <h2
              id="store-selector-title"
              className="px-10 py-2 text-start text-lg font-bold text-black lg:px-8 lg:py-6"
            >
              Pick a Store
            </h2>

            {/* Scrollable store list */}
            <div
              ref={modalScrollRef}
              className="min-h-0 overflow-y-auto px-6 pt-2 pb-6 lg:px-8 lg:pb-8"
              role="listbox"
              aria-label="Available stores"
              tabIndex={0}
            >
              <div className="grid gap-2 lg:gap-4">
                {stores.map((store) => (
                  <button
                    key={store.value}
                    onClick={() => setChosenStore(store.value)}
                    disabled={store.comingSoon}
                    className={`flex w-full justify-between gap-4 p-4 px-6 shadow-sm shadow-gray-400 transition focus:ring-2 focus:outline-none focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] ${
                      store.comingSoon
                        ? 'cursor-not-allowed bg-gray-100 text-black opacity-60'
                        : chosenStore === store.value
                          ? 'bg-catalystdarkblue text-white'
                          : 'text-black hover:bg-gray-100'
                    }`}
                    role="option"
                    aria-selected={chosenStore === store.value}
                    aria-disabled={store.comingSoon}
                  >
                    <div className="text-start">
                      <p className="text-sm font-bold">{store.name.toUpperCase()}</p>
                      <p className="font-helvetica-neue text-xs">{store.hours}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {store.comingSoon && (
                        <span className="rounded bg-yellow-400 px-2 py-0.5 text-xs font-bold whitespace-nowrap text-black">
                          COMING SOON
                        </span>
                      )}
                      <div className="max-w-34 text-right">
                        <p className="font-helvetica-neue text-xs">
                          {store.address}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-center border-t bg-white p-4 lg:px-8 lg:py-6">
              <button
                disabled={!stores.some(store => store.value === chosenStore && !store.comingSoon)}
                onClick={() => handleStoreSelection(chosenStore)}
                className="bg-catalystdarkblue hover:bg-catalystblue min-h-[44px] min-w-[44px] rounded px-6 py-2 text-white transition focus:ring-2 focus:outline-none focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Shop Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModalVisible && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-change-title"
          aria-describedby="confirm-change-description"
          className="fixed inset-0 z-101 flex items-center justify-center bg-black/50"
          onClick={cancelConfirmation}
        >
          <div
            ref={confirmModalFocusTrapRef}
            className="w-[90%] max-w-md rounded-lg bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
            role="document"
          >
            <h3 id="confirm-change-title" className="mb-4 text-2xl font-bold text-black">
              Confirm Store Change
            </h3>
            <p id="confirm-change-description" className="mb-4 text-lg text-black">
              You are currently shopping at{' '}
              <strong className="underline">{currentStore ? currentStore.name : 'None'}</strong> and
              you are switching to{' '}
              <strong className="underline">{pendingStoreObj ? pendingStoreObj.name : ''}</strong>.
              {pendingRedirect
                ? location.pathname.includes('/dispensary-locations/')
                  ? " You will be redirected to the selected store's location page. "
                  : ' You will be redirected to the homepage. '
                : ' '}
              Changing store will clear your current cart. Do you want to proceed?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="min-h-[44px] min-w-[44px] rounded bg-gray-300 px-4 py-2 focus:ring-2 focus:outline-none focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                onClick={cancelConfirmation}
              >
                Cancel
              </button>
              <button
                className="bg-catalystdarkblue hover:bg-catalystblue min-h-[44px] min-w-[44px] rounded px-4 py-2 text-white transition focus:ring-2 focus:outline-none focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)]"
                onClick={confirmStoreChange}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
